import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * The three steps the outpatient flow walks through, shown the way mobile's CareStepIndicator does:
 * the user can see where they are and what is still to come before payment.
 *
 * Video consultation has no indicator — the app picks an expert and filters by category on one
 * page, so there is no sequence to show.
 */
export const BookingSteps = ({ current }: { current: 1 | 2 | 3 }) => {
    const { t } = useTranslation()

    const steps = [
        t('frontend.steps.service'),
        t('frontend.steps.expert'),
        t('frontend.steps.slot'),
    ]

    return (
        <ol className="mb-10 flex items-center">
            {steps.map((label, index) => {
                const step = index + 1
                const isDone = step < current
                const isActive = step === current

                return (
                    <li key={label} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center gap-2">
                            <span
                                className={`flex size-8 items-center justify-center rounded-full text-sm font-bold ${
                                    isDone || isActive
                                        ? 'bg-primary text-white'
                                        : 'bg-slate-200 text-brand-gray'
                                }`}
                            >
                                {isDone ? <Check className="size-4" /> : step}
                            </span>
                            <span
                                className={`text-xs font-semibold ${
                                    isActive ? 'text-brand-dark' : 'text-brand-gray'
                                }`}
                            >
                                {label}
                            </span>
                        </div>
                        {step < steps.length && (
                            <div
                                className={`mx-3 mb-6 h-0.5 flex-1 ${
                                    isDone ? 'bg-primary' : 'bg-slate-200'
                                }`}
                            />
                        )}
                    </li>
                )
            })}
        </ol>
    )
}

export const SelectedValue = ({
    label,
    value,
    onChange,
    changeLabel,
}: {
    label: string
    value: string
    onChange?: () => void
    changeLabel: string
}) => (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand-gray">
                {label}
            </span>
            <span className="font-semibold text-brand-dark">{value}</span>
        </div>
        {onChange && (
            <button type="button" className="text-sm font-semibold text-primary" onClick={onChange}>
                {changeLabel}
            </button>
        )}
    </div>
)
